// Lib
import React from 'react';
import { createPortal } from 'react-dom';
import type { ReactPortal } from 'react';
import type { GridSettings, CellProperties } from 'handsontable/settings';
import { isEqual } from 'lodash';

// Components
import MilanoteCellRendererComponent from '../modules/MilanoteCellRendererComponent';

// Utils
import { getCellBackgroundColor } from '../../bridge/utils/dataUtils/cell/tableCellStyleUtils';
import { getTableCellVerticalAlignmentClassName } from '../utils/domUtils/tableCellVerticalAlignmentClassNameUtils';
import { colorBorders } from '../utils/domUtils/tableCellBorderUtils';

// Types
import type { CellData, HotComputedCellValue, TableDataVersion } from '../../../../../common/table/TableTypes';
import type { MNHotInstance } from '../../bridge/tableHotInstanceTypes';

type HandsontableHook<Key extends keyof GridSettings> = NonNullable<GridSettings[Key]>;

type Props = {
    elementId: string;
    version: TableDataVersion;
    locale: string;
    filterQuery?: string;
};

type State = {
    portals: RendererPortal[];
};

type RendererPortal = ReactPortal & {
    containerInfo: HTMLElement;
};

type RendererProps = {
    elementId: string;
    row: number;
    col: number;
    cellData?: CellData;
    hotTableInstance: MNHotInstance;
    hotComputedCellValue: HotComputedCellValue;
    version: TableDataVersion;
    locale: string;
    filterQuery?: string;
    checkboxRendererVersion: number | undefined;
};

class HotTableRendererPortalManager extends React.Component<Props, State> {
    portalCacheMap = new Map<string, RendererPortal>();

    state: State = { portals: [] };

    commitCacheToState = (): void => {
        if (this.portalCacheMap.size === 0) return;

        const portals = [...this.portalCacheMap.values()];
        this.portalCacheMap.clear();
        this.setState({ portals });
    };

    afterViewRender: HandsontableHook<'afterViewRender'> = () => {
        this.commitCacheToState();
    };

    initGetMemoisedPortal = () => {
        const memoisationPropsMap: Record<string, RendererProps | undefined> = {};
        const memoisationPortalMap: Record<string, RendererPortal | undefined> = {};

        return (TD: HTMLTableCellElement, props: RendererProps): RendererPortal | undefined => {
            const { row, col } = props;

            const memoisationKey = `${row}-${col}`;

            // Ghost tables are used to calculate the width of the table columns.
            // In this case, we only want to return the cached portal (if any).
            if (TD.getAttribute('ghost-table')) return memoisationPortalMap[memoisationKey];

            const lastProps = memoisationPropsMap[memoisationKey];
            const propsHaveChanged = !lastProps || !isEqual(lastProps, props);
            if (!propsHaveChanged && TD.innerHTML !== '') return memoisationPortalMap[memoisationKey];

            const portal = createPortal(
                <MilanoteCellRendererComponent {...props} />,
                TD,
                memoisationKey,
            ) as RendererPortal;

            memoisationPropsMap[memoisationKey] = props;
            memoisationPortalMap[memoisationKey] = portal;

            return portal;
        };
    };

    getMemoisedPortal = this.initGetMemoisedPortal();

    getRendererWrapper = (): HandsontableHook<'renderer'> => {
        return (
            hotTableInstance,
            TD: HTMLTableCellElement,
            row: number,
            col: number,
            _prop: string | number,
            hotComputedCellValue: HotComputedCellValue,
            cellProperties: CellProperties & { cellData?: CellData; checkboxRendererVersion?: number },
        ): void => {
            const milanoteHot = hotTableInstance as MNHotInstance;
            const { cellData, checkboxRendererVersion } = cellProperties;
            const { elementId, version, locale, filterQuery } = this.props;

            const portal = this.getMemoisedPortal(TD, {
                elementId,
                row,
                col,
                cellData,
                hotTableInstance: milanoteHot,
                hotComputedCellValue,
                version,
                locale,
                filterQuery,
                checkboxRendererVersion,
            });

            if (!portal) return;

            // Ghost tables are used to calculate the width of the table columns.
            // We want to match the content to the real table, in order for the col widths to be calculated accurately.
            if (TD.getAttribute('ghost-table')) {
                TD.innerHTML = portal.containerInfo.innerHTML || '';
                return;
            }

            this.portalCacheMap.set(`${row}-${col}`, portal);

            TD.classList.add(getTableCellVerticalAlignmentClassName(cellData?.verticalAlignment));
            TD.style.setProperty('--ws-table-cell-background-color', getCellBackgroundColor(cellData));

            colorBorders(milanoteHot, row, col);

            // on the last cell, update state immediately
            const maxRow = hotTableInstance.countRows() - 1;
            const maxCol = hotTableInstance.countCols() - 1;
            if (row === maxRow && col === maxCol) {
                this.commitCacheToState();
            }
        };
    };

    render(): React.JSX.Element {
        return <div className="HotTableRendererPortalManager">{this.state.portals}</div>;
    }
}

export default HotTableRendererPortalManager;
